import * as React from 'react';

import { useSendPhoneVerificationCode } from './mutations/useSendPhoneVerificationCode';
import { useVerifyPhoneNumber } from './mutations/useVerifyPhoneNumber';
import useCountdown from './useCountdown';

const COOLDOWN_SECONDS = 120;

interface IUsePhoneVerificationReturn {
  code: string;
  setCode: React.Dispatch<React.SetStateAction<string>>;
  error: string;
  codeSent: boolean;
  isSending: boolean;
  isVerifying: boolean;
  secondsRemaining: number;
  isCountdownActive: boolean;
  handleSendCode: () => Promise<void>;
  handleVerify: (code: string) => Promise<void>;
  /**
   * Start the resend countdown without sending a code. Use when a previous
   * step already triggered the send (e.g. an OnboardingFlow handoff where
   * entry sends the code, then verify mounts and just needs the timer).
   */
  startCountdown: () => void;
  reset: () => void;
}

/**
 * Manages phone verification flow state: sending a code, verifying it,
 * countdown cooldown, and error handling.
 *
 * @param onSuccess - Callback invoked after the phone number is successfully verified.
 * @returns Phone verification state and handlers.
 */
const usePhoneVerification = (onSuccess: () => void): IUsePhoneVerificationReturn => {
  const [code, setCode] = React.useState('');
  const [error, setError] = React.useState('');
  const [codeSent, setCodeSent] = React.useState(false);
  const countdown = useCountdown();
  const onSuccessRef = React.useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  const [sendCode, { loading: isSending }] = useSendPhoneVerificationCode();
  const [verifyCode, { loading: isVerifying }] = useVerifyPhoneNumber();

  const handleSendCode = React.useCallback(async () => {
    setError('');
    try {
      await sendCode();
      setCodeSent(true);
      countdown.start(COOLDOWN_SECONDS);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send code';
      setError(message);
    }
  }, [sendCode, countdown]);

  const handleVerify = React.useCallback(
    async (verifyCode_: string) => {
      if (!verifyCode_.trim()) {
        return;
      }
      setError('');
      try {
        await verifyCode({ variables: { input: { code: verifyCode_.trim() } } });
        onSuccessRef.current();
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Verification failed';
        setError(message);
        setCode('');
      }
    },
    [verifyCode]
  );

  const startCountdown = React.useCallback(() => {
    countdown.start(COOLDOWN_SECONDS);
  }, [countdown]);

  const reset = React.useCallback(() => {
    setCode('');
    setError('');
    setCodeSent(false);
    countdown.reset();
  }, [countdown]);

  return {
    code,
    setCode,
    error,
    codeSent,
    isSending,
    isVerifying,
    secondsRemaining: countdown.secondsRemaining,
    isCountdownActive: countdown.isActive,
    handleSendCode,
    handleVerify,
    startCountdown,
    reset,
  };
};

export default usePhoneVerification;
